import { useEffect, useMemo, useState } from 'react';

import useSavedAddresses from '@/hooks/use-saved-addresses';

import type { AddressType, CreateSavedAddressReq, SavedAddress } from '@/types/parcel/userData';

interface UseSavedAddressesModalProps {
  open: boolean;
  targetType?: AddressType;
  onSelect: (address: SavedAddress) => void;
  onClose: () => void;
}

export const useSavedAddressesModal = (props: UseSavedAddressesModalProps) => {
  const { open, targetType, onSelect, onClose } = props;

  const savedAddressesHook = useSavedAddresses({ initialType: targetType });

  const [selectedTab, setSelectedTab] = useState<'all' | 'origin' | 'destination'>(
    targetType === 'origin' ? 'origin' : targetType === 'destination' ? 'destination' : 'all',
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [editingAddress, setEditingAddress] = useState<SavedAddress | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [deletingAddress, setDeletingAddress] = useState<SavedAddress | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (open) {
      void savedAddressesHook.fetchAddresses(targetType);
      if (targetType) {
        setSelectedTab(targetType === 'origin' ? 'origin' : 'destination');
      }
    }
  }, [open, targetType, savedAddressesHook.fetchAddresses]);

  const filteredAddresses = useMemo(() => {
    let result = savedAddressesHook.addresses;

    // Filtro por pestaña seleccionada
    if (selectedTab === 'origin') {
      result = result.filter((item) => item.type === 'origin' || item.type === 'both');
    } else if (selectedTab === 'destination') {
      result = result.filter((item) => item.type === 'destination' || item.type === 'both');
    }

    // Filtro por texto de búsqueda
    const query = searchQuery.trim().toLowerCase();
    if (query) {
      result = result.filter((item) => {
        const aliasMatch = item.alias?.toLowerCase().includes(query);
        const addressMatch = item.address?.toLowerCase().includes(query);
        const colonyMatch = item.colony?.toLowerCase().includes(query);
        const cityMatch = item.city?.toLowerCase().includes(query);
        const contactName =
          `${item.contact?.first_name || ''} ${item.contact?.last_name || ''}`.toLowerCase();
        const contactMatch = contactName.includes(query);
        const phoneMatch = item.contact?.phone?.includes(query);
        const emailMatch = item.contact?.email?.toLowerCase().includes(query);

        return (
          aliasMatch ||
          addressMatch ||
          colonyMatch ||
          cityMatch ||
          contactMatch ||
          phoneMatch ||
          emailMatch
        );
      });
    }

    return result;
  }, [savedAddressesHook.addresses, selectedTab, searchQuery]);

  const handleSelectAddress = (address: SavedAddress) => {
    // 1. Enviar ping en segundo plano para renovar vigencia a 7 días
    void savedAddressesHook.pingAddress(address.id);
    // 2. Notificar al formulario para autocompletar
    onSelect(address);
    onClose();
  };

  const handleOpenCreate = () => {
    setEditingAddress(null);
    setIsFormOpen(true);
  };

  const handleOpenEdit = (address: SavedAddress) => {
    setEditingAddress(address);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingAddress(null);
  };

  const handleSaveForm = async (data: CreateSavedAddressReq) => {
    if (editingAddress) {
      await savedAddressesHook.updateAddress(editingAddress.id, data);
    } else {
      await savedAddressesHook.createAddress(data);
    }
  };

  const handleOpenDelete = (address: SavedAddress) => {
    setDeletingAddress(address);
  };

  const handleCloseDelete = () => {
    setDeletingAddress(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletingAddress) return;
    setIsDeleting(true);
    try {
      await savedAddressesHook.deleteAddress(deletingAddress.id);
      setDeletingAddress(null);
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    addresses: filteredAddresses,
    loading: savedAddressesHook.loading,
    error: savedAddressesHook.error,
    selectedTab,
    setSelectedTab,
    searchQuery,
    setSearchQuery,
    isFormOpen,
    editingAddress,
    deletingAddress,
    isDeleting,
    handleSelectAddress,
    handleOpenCreate,
    handleOpenEdit,
    handleCloseForm,
    handleSaveForm,
    handleOpenDelete,
    handleCloseDelete,
    handleConfirmDelete,
    defaultType: targetType || 'both',
  };
};

export default useSavedAddressesModal;
